import { useEffect, useState, useRef } from "react";
import { Button } from "../../components/Button";
import { InfoBox } from "../../components/form/InfoBox";
import { CCPAForm } from "./components/CCPAForm";
import { DSARForm } from "./components/DSARForm";
import { validate } from "../../../utils/form-functions";
import { sendGaEvent } from "../../../../config/userTracking";
import { userApi } from "../../../../api/userApi";
import ReCAPTCHA from "react-google-recaptcha";
import { animatePresenceConfig, appConfig } from "../../../../config/appConfig";
import { AnimatePresence, motion } from "framer-motion";
import { serializeError } from "../../../../utils/serializeError";
import { MessageBox } from "../../../../platform/components/MessageBox/MessageBox";

export const DataFormTemplate = ({ type, data }) => {
  const [modalOpen, changeModalOpen] = useState(false);
  const [errorModalOpen, changeErrorModalOpen] = useState(false);
  const [formData, changeFormData] = useState({});
  const [errors, changeErrors] = useState({});
  const [sending, changeSending] = useState(false);
  const [isRecaptchaRequired, setIsRecaptchaRequired] = useState(false);
  const [error, setError] = useState(null);
  const [captchaResponse, setCaptchaResponse] = useState(null);
  const recaptchaRef = useRef(null);

  const onCaptchaChange = captchaResponse => {
    setCaptchaResponse(captchaResponse);
  };

  const checkIfCaptchaIsRequired = async () => {
    try {
      const {
        data: { next_submit_requires_captcha },
      } = await userApi.isDataFormTemplateCaptchaNeeded(type);

      if (next_submit_requires_captcha) {
        setIsRecaptchaRequired(true);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const changeData = (e, data) => {
    let newFormData = { ...formData };
    newFormData[data.id] = e;
    changeFormData(newFormData);
  };

  const preparedData = () => {
    let preparedData = {
      body: `${type[0].toUpperCase()}${type.slice(1)} Report`,
    };
    data.fields.forEach(field => {
      preparedData[field.id] = formData[field.id];
    });
    // ORC-1402: See ticket prior to changing
    if (formData.org_contact_ref) {
      preparedData.org_contact_ref = formData.org_contact_ref;
    }
    return preparedData;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (validate({ type, formData, data, changeErrors }) === true) {
      changeSending(true);
      setError(null);
      const dataToSend = {
        type,
        formValues: preparedData(),
        g_recaptcha_response: captchaResponse || undefined,
      };
      sendGaEvent({
        action: "Form Submission",
        category: "Privacy Request",
        label: `Submit - ${type[0].toUpperCase()}${type.slice(1)} Form - /${type}`,
      });
      try {
        await userApi.sendFrontForm(dataToSend);
        changeModalOpen(true);
        changeSending(false);
        recaptchaRef.current?.reset();
        addNewFormData();
        setIsRecaptchaRequired(true);
      } catch (e) {
        await checkIfCaptchaIsRequired();
        let serialize_error = serializeError(e);
        if (serialize_error.message.includes("CAPTCHA")) {
          setError(serialize_error);
        } else {
          changeErrorModalOpen(true);
        }

        changeSending(false);
      }
    }
  };

  const addNewFormData = () => {
    let newFormData = {};
    data.fields.forEach(field => {
      newFormData[field.id] = "";
      if (field.type === "checkbox") newFormData[field.id] = false;
    });
    changeFormData(newFormData);
  };

  useEffect(() => {
    addNewFormData();
    checkIfCaptchaIsRequired();
  }, []);

  return (
    <section className="data_template-background">
      <section className="section-layout">
        {modalOpen && <InfoBox closeModal={() => changeModalOpen(false)} />}
        {errorModalOpen && <InfoBox closeModal={() => changeErrorModalOpen(false)} type="Error" />}
        <section className={`data_template-main ${type === "CCPA" ? "ccpa" : ""}`}>
          {type === "CCPA" && (
            <section>
              <h1 className="data_template-title">{data.title}</h1>
              <p className="data_template-paragraph">
                {data.header_1}
                <a href={`mailto:${data.link_target}`}>{data.link}</a>
                {data.header_2}
              </p>
              <p className="data_template-paragraph">{data.paragraph_1}</p>
              <p className="data_template-paragraph">{data.paragraph_2}</p>
            </section>
          )}
          {type === "DSAR" && (
            <section>
              <h1 className="data_template-title">{data.title}</h1>
              <p className="data_template-paragraph">{data.paragraph}</p>
            </section>
          )}
          <section>
            <form onSubmit={handleSubmit}>
              {type === "CCPA" && typeof formData[data.fields[0].id] !== "undefined" && (
                <CCPAForm data={data} formData={formData} changeData={changeData} errors={errors} />
              )}
              {type === "DSAR" && typeof formData[data.fields[0].id] !== "undefined" && (
                <DSARForm data={data} formData={formData} changeData={changeData} errors={errors} />
              )}
              {isRecaptchaRequired && (
                <div className="DataFormTemplate__captchaWrapper">
                  <ReCAPTCHA
                    sitekey={appConfig.CAPTCHA_KEY}
                    onChange={onCaptchaChange}
                    ref={recaptchaRef}
                  />
                </div>
              )}

              {!!error && (
                <AnimatePresence>
                  <motion.div
                    className="DataFormTemplate__errorMessageWrapper"
                    {...animatePresenceConfig}
                  >
                    <MessageBox>{error.message}</MessageBox>
                  </motion.div>
                </AnimatePresence>
              )}
              <Button disabled={sending}>{data.form.button_text}</Button>
            </form>
          </section>
        </section>
      </section>
    </section>
  );
};
